import { Globe } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PublishedBadgeProps {
  /** KB-membership marker — the badge renders nothing without it. */
  publishedAt?: string;
  /** Draft's last save; newer than publishedAt means the KB is behind. */
  updatedAt?: string;
  /** Link target (the document's Knowledge Base page); omit in clickable rows. */
  href?: string;
  className?: string;
}

/**
 * Knowledge Base membership chip (kb-publish R6): green when the published
 * state matches the draft, amber when edits have moved past the last publish.
 */
export function PublishedBadge({ publishedAt, updatedAt, href, className }: PublishedBadgeProps) {
  if (!publishedAt) return null;
  const behind = !!updatedAt && updatedAt > publishedAt;
  const label = behind ? 'Published · draft ahead' : 'Published';
  const chipClass = cn(
    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
    behind
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
      : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    href && 'hover:underline',
    className,
  );
  const inner = (
    <>
      <Globe className="w-3 h-3 shrink-0" />
      {label}
    </>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={chipClass} title="View in the Knowledge Base">
        {inner}
      </a>
    );
  }
  return (
    <span className={chipClass} title={behind ? 'The draft has changed since the last publish' : 'In the Knowledge Base'}>
      {inner}
    </span>
  );
}
